import React, { useMemo, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { FiCheck, FiEye, FiX, FiUser, FiFile, FiCamera, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { Badge, Button, Card, CardBody, CardHeader, CardFooter, Col, Container, Input, Row, Table, Modal, ModalHeader, ModalBody, ModalFooter, TabContent, TabPane, Nav, NavItem, NavLink, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Pagination, PaginationItem, PaginationLink } from 'reactstrap';
import { getGuideVerification, approveGuideVerification, rejectGuideVerification, getGuideVerificationById } from "../../api/ApiServices"
import CustomDropdown from 'components/Common/CustomDropdown';
import CustomPagination from 'components/Common/CustomPagination';
import LoadingDots from 'components/Common/LoadingDots';
import CommonHeader from 'components/Headers/CommonHeader';
import { useSocket } from "../../context/SocketContext";
import {
    InputGroup,
    InputGroupAddon,
    InputGroupText
} from "reactstrap";

const ITEMS_PER_PAGE = 10;

const GuideManagement = () => {
    const location = useLocation();
    const [guides, setGuides] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState(location.state?.statusFilter || 'all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedGuide, setSelectedGuide] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('1');
    const [rejectionReason, setRejectionReason] = useState('');
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectGuideUuid, setRejectGuideUuid] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [viewLoading, setViewLoading] = useState(false);

    // Pagination & Summary state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [summaryStats, setSummaryStats] = useState({ total: 0, approved: 0, pending: 0, rejected: 0 });

    // Base URL for images
    const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || "";

    // Helper function to get image URL
    const getImageUrl = (imageData, defaultFolder = "guide-verification") => {
        if (!imageData) return null;

        let clean = String(imageData).trim().replace(/^["']|["']$/g, "").replace(/%22$/gi, "");
        if (!clean) return null;

        if (clean.startsWith('http') || clean.startsWith('data:')) {
            return clean;
        }

        const cleanPath = clean.replace(/^\/+/, "");
        if (cleanPath.startsWith(`${defaultFolder}/`)) {
            return `https://areatrek-live-bucket.s3.us-east-2.amazonaws.com/${cleanPath}`;
        }

        return `https://areatrek-live-bucket.s3.us-east-2.amazonaws.com/${defaultFolder}/${cleanPath}`;
    };

    // Update status filter & search from route state (e.g. from Dashboard KPI cards or notifications)
    useEffect(() => {
        if (location.state?.statusFilter) {
            setStatusFilter(location.state.statusFilter);
        }
        if (location.state?.searchTerm) {
            setSearchTerm(location.state.searchTerm);
        }
    }, [location.state]);

    // Handle direct notification click event (even if already on this page)
    useEffect(() => {
        const handleNotificationNav = (e) => {
            const detail = e.detail || {};
            if (detail.status) {
                setStatusFilter(detail.status);
            }
            if (detail.guideName) {
                setSearchTerm(detail.guideName);
            } else if (detail.guideEmail) {
                setSearchTerm(detail.guideEmail);
            }
            setCurrentPage(1);
            fetchGuides(1, detail.guideName || detail.guideEmail || '', detail.status === 'all' ? '' : (detail.status || ''));
        };

        window.addEventListener("verificationNotificationClicked", handleNotificationNav);
        return () => {
            window.removeEventListener("verificationNotificationClicked", handleNotificationNav);
        };
    }, []);

    // Auto-open guide verification modal if navigated from notification
    useEffect(() => {
        if (!location.state?.autoOpen) return;
        const targetUuid = location.state.verificationUuid || location.state.guideUuid;
        if (!targetUuid || !guides.length) return;

        const targetGuide = guides.find(
            (g) => g.uuid === targetUuid || g.userUuid === targetUuid || g.userId === targetUuid
        );
        if (targetGuide) {
            handleViewGuide(targetGuide.id);
        }
    }, [guides, location.state]);

    // Fetch guide verifications on component mount and filter/page changes
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchGuides(currentPage, searchTerm, statusFilter);
        }, 300);
        return () => clearTimeout(timer);
    }, [currentPage, searchTerm, statusFilter]);

    // Real-time listener: Auto-update table & summary stats when new verification arrives without manual page refresh
    const socket = useSocket();
    useEffect(() => {
        const handleRealtimeUpdate = (data) => {
            console.log("🔔 Real-time verification event in GuideManagement table:", data);
            fetchGuides(currentPage, searchTerm, statusFilter);
        };

        if (socket) {
            socket.on("new_verification_submitted", handleRealtimeUpdate);
            socket.on("verification_status_updated", handleRealtimeUpdate);
        }
        window.addEventListener("newVerificationSubmitted", handleRealtimeUpdate);

        return () => {
            if (socket) {
                socket.off("new_verification_submitted", handleRealtimeUpdate);
                socket.off("verification_status_updated", handleRealtimeUpdate);
            }
            window.removeEventListener("newVerificationSubmitted", handleRealtimeUpdate);
        };
    }, [socket, currentPage, searchTerm, statusFilter]);

    const fetchGuides = async (page = 1, search = searchTerm, status = statusFilter) => {
        try {
            setLoading(true);
            setError(null);
            const response = await getGuideVerification({
                page,
                limit: ITEMS_PER_PAGE,
                search,
                status: status === 'all' ? '' : status
            });

            if (response.success) {
                const transformedGuides = (response.data || []).map(verification => ({
                    id: verification._id,
                    uuid: verification.uuid,

                    // User details
                    userId: verification.user?._id || null,
                    userUuid: verification.user?.uuid || verification.userId || null,
                    name: verification.user?.fullName || 'Unknown',
                    email: verification.user?.email || 'No email',
                    mobileNumber: verification.user?.mobileNumber || 'N/A',
                    gender: verification.user?.gender || 'N/A',
                    city: verification.user?.location || 'Not specified',
                    country: verification.user?.country || 'Not specified',
                    profileImage: verification.user?.profileImage || '',

                    // Verification details
                    status: verification.status
                        ? verification.status.charAt(0).toUpperCase() +
                        verification.status.slice(1)
                        : 'Pending',

                    documentFrontImage: verification.documentFrontImage,
                    documentBackImage: verification.documentBackImage,
                    selfieImage: verification.selfieImage,

                    livenessPassed: verification.livenessPassed,
                    livenessScore: verification.livenessScore || 0,

                    rejectionReason: verification.rejectionReason,

                    createdAt: verification.createdAt,
                    updatedAt: verification.updatedAt,

                    reviewedBy: verification.reviewedBy,
                    reviewedAt: verification.reviewedAt,

                    isActive: verification.user?.isActive,
                    type: verification.user?.type
                }));
                setGuides(transformedGuides);
                if (response.pagination) {
                    setTotalItems(response.pagination.totalRecords ?? transformedGuides.length);
                    setTotalPages(response.pagination.totalPages ?? 1);
                } else {
                    setTotalItems(transformedGuides.length);
                    setTotalPages(1);
                }
                if (response.summary) {
                    setSummaryStats(response.summary);
                }
            } else {
                setError(response.message || 'Failed to fetch guides');
            }
        } catch (err) {
            console.error('Error fetching guides:', err);
            setError('Failed to load guide verifications');
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleStatusFilterChange = (value) => {
        setStatusFilter(value);
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const updateStatus = async (uuid, status, reason = null) => {
        try {
            setActionLoading(true);
            let response;

            if (status === 'approved') {
                response = await approveGuideVerification(uuid);
            } else if (status === 'rejected') {
                response = await rejectGuideVerification(uuid, reason);
            }

            if (response && response.success) {
                // Update the local state with the new status
                setGuides((prev) =>
                    prev.map((guide) =>
                        guide.uuid === uuid ? {
                            ...guide,
                            status: status.charAt(0).toUpperCase() + status.slice(1),
                            updatedAt: new Date().toISOString(),
                            rejectionReason: reason || guide.rejectionReason
                        } : guide
                    )
                );

                // Close modals if open
                setShowRejectModal(false);
                setRejectGuideUuid(null);
                setRejectionReason('');

                // Refresh the list to get updated data from server
                await fetchGuides(currentPage, searchTerm, statusFilter);

                // If the guide is currently selected in the modal, update it
                if (selectedGuide && selectedGuide.uuid === uuid) {
                    setModalOpen(false);
                    const guide = guides.find(g => g.uuid === uuid);
                    if (guide) {
                        await handleViewGuide(guide.id);
                    }
                }
            } else {
                setError(response?.message || `Failed to ${status} guide`);
            }
        } catch (err) {
            console.error(`Error ${status}ing guide:`, err);
            setError(`Failed to ${status} guide verification`);
        } finally {
            setActionLoading(false);
        }
    };

    const getStatusColor = (status) => {
        if (status === 'Approved') return 'success';
        if (status === 'Pending') return 'warning';
        if (status === 'Rejected') return 'danger';
        return 'secondary';
    };

    const getStatusIcon = (status) => {
        if (status === 'Approved') return <FiCheckCircle className="text-success" />;
        if (status === 'Rejected') return <FiXCircle className="text-danger" />;
        return null;
    };
    const getTypeBadgeClass = (type) => {
        if (type === "traveler") return "badge-traveler";
        if (type === "guide") return "badge-guide";
        return "badge-default";
    };

    const getStatusBadgeClass = (isActive) => {
        return isActive ? "badge-active" : "badge-inactive";
    };
    const handleViewGuide = async (id) => {
        const guide = guides.find((g) => g.id === id);

        if (!guide) {
            console.error("Guide not found:", id);
            setError("Guide not found");
            return;
        }

        setSelectedGuide(guide);
        setModalOpen(true);
        setActiveTab("1");

        try {
            setViewLoading(true);
            setError(null);

            const verificationId = guide?.uuid || guide?.id || guide?._id;
            const response = await getGuideVerificationById(verificationId);


            if (response.success) {
                const verification = response.data;



                // IMPORTANT:
                // API response has `user`, NOT `userId`
                const user = verification.user || {};

                const detailedGuide = {
                    id: verification._id,
                    uuid: verification.uuid,

                    // User Details
                    userId: user._id,
                    name: user.fullName || "Unknown",
                    email: user.email || "No email",
                    mobileNumber: user.mobileNumber || "N/A",
                    gender: user.gender || "N/A",
                    city: user.location || "Not specified",
                    country: user.country || "Not specified",

                    // Verification Details
                    status: verification.status
                        ? verification.status.charAt(0).toUpperCase() +
                        verification.status.slice(1)
                        : "Pending",

                    documentFrontImage: verification.documentFrontImage,
                    documentBackImage: verification.documentBackImage,
                    selfieImage: verification.selfieImage,

                    livenessPassed: verification.livenessPassed,
                    livenessScore: verification.livenessScore || 0,

                    rejectionReason: verification.rejectionReason,

                    createdAt: verification.createdAt,
                    updatedAt: verification.updatedAt,

                    reviewedBy: verification.reviewedBy,
                    reviewedAt: verification.reviewedAt,

                    // User status
                    isActive: user.isActive,
                    type: user.type,

                    // These may not currently exist in API
                    isGuideVerified: user.isGuideVerified || false,
                    hasGuideBadge: user.hasGuideBadge || false,
                    isGuideVerificationPending:
                        user.isGuideVerificationPending || false,

                    // Profile
                    profileImage: user.profileImage || null,
                    coverImage: user.coverImage || null,
                };



                setSelectedGuide(detailedGuide);
                setModalOpen(true);
                setActiveTab("1");
            } else {
                console.error("API Error:", response.message);
                setError(response.message || "Failed to load guide details");
            }
        } catch (err) {
            console.error("Error fetching guide details:", err);
            setError("Failed to load guide details");
        } finally {
            setViewLoading(false);
        }
    };

    // Handle reject with reason - now uses UUID
    const handleRejectClick = (uuid) => {
        setRejectGuideUuid(uuid);
        setRejectionReason('');
        setShowRejectModal(true);
        if (modalOpen) {
            setModalOpen(false);
        }
    };

    const handleConfirmReject = async () => {
        if (rejectGuideUuid) {
            await updateStatus(rejectGuideUuid, 'rejected', rejectionReason || 'No reason provided');
        }
    };

    // Calculate statistics
    const totalGuides = summaryStats.total || totalItems;
    const approvedCount = summaryStats.approved || 0;
    const pendingCount = summaryStats.pending || 0;
    const rejectedCount = summaryStats.rejected || 0;


    if (error && guides.length === 0) {
        return (
            <Container fluid className="mt--7">
                <Card className="shadow border-0">
                    <CardBody className="text-center py-5">
                        <div className="text-danger mb-2">
                            <i className="fas fa-exclamation-circle" style={{ fontSize: '3rem' }}></i>
                        </div>
                        <h4>Error Loading Guides</h4>
                        <p className="text-muted">{error}</p>
                        <Button color="primary" onClick={fetchGuides}>
                            Try Again
                        </Button>
                    </CardBody>
                </Card>
            </Container>
        );
    }

    return (
        <>
            <CommonHeader />
            <Container fluid className="mt--7">
                <Row className="mb-4">
                    <Col xs="12" sm="4" md="4" className="mb-3 mb-sm-0">
                        <Card
                            className="card-stats shadow-sm border-0 h-100 rounded-lg"
                            role="button"
                            tabIndex={0}
                            onClick={() => handleStatusFilterChange('all')}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleStatusFilterChange('all');
                                }
                            }}
                            style={{
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                                border: statusFilter === 'all' ? '2px solid #5e72e4' : '1px solid transparent',
                                transform: statusFilter === 'all' ? 'translateY(-3px)' : 'none',
                            }}
                        >
                            <CardBody className="py-3 px-4">
                                <Row className="align-items-center">
                                    <div className="col">
                                        <span className="text-muted text-xs text-uppercase font-weight-bold d-block mb-1">Total Guides</span>
                                        <span className="h2 mb-0 font-weight-bold text-dark">{totalGuides}</span>
                                    </div>
                                    <Col className="col-auto">
                                        <div className="icon icon-shape bg-gradient-primary text-white rounded-circle shadow">
                                            <i className="fas fa-user-tie" />
                                        </div>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col xs="12" sm="4" md="4" className="mb-3 mb-sm-0">
                        <Card
                            className="card-stats shadow-sm border-0 h-100 rounded-lg"
                            role="button"
                            tabIndex={0}
                            onClick={() => handleStatusFilterChange('approved')}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleStatusFilterChange('approved');
                                }
                            }}
                            style={{
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                                border: statusFilter === 'approved' ? '2px solid #2dce89' : '1px solid transparent',
                                transform: statusFilter === 'approved' ? 'translateY(-3px)' : 'none',
                            }}
                        >
                            <CardBody className="py-3 px-4">
                                <Row className="align-items-center">
                                    <div className="col">
                                        <span className="text-muted text-xs text-uppercase font-weight-bold d-block mb-1">Approved</span>
                                        <span className="h2 mb-0 font-weight-bold text-success">{approvedCount}</span>
                                    </div>
                                    <Col className="col-auto">
                                        <div className="icon icon-shape bg-gradient-success text-white rounded-circle shadow">
                                            <i className="fas fa-check-circle" />
                                        </div>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col xs="12" sm="4" md="4" className="mb-3 mb-sm-0">
                        <Card
                            className="card-stats shadow-sm border-0 h-100 rounded-lg"
                            role="button"
                            tabIndex={0}
                            onClick={() => handleStatusFilterChange('pending')}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleStatusFilterChange('pending');
                                }
                            }}
                            style={{
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                                border: statusFilter === 'pending' ? '2px solid #fb6340' : '1px solid transparent',
                                transform: statusFilter === 'pending' ? 'translateY(-3px)' : 'none',
                            }}
                        >
                            <CardBody className="py-3 px-4">
                                <Row className="align-items-center">
                                    <div className="col">
                                        <span className="text-muted text-xs text-uppercase font-weight-bold d-block mb-1">Pending</span>
                                        <span className="h2 mb-0 font-weight-bold text-warning">{pendingCount}</span>
                                    </div>
                                    <Col className="col-auto">
                                        <div className="icon icon-shape bg-gradient-warning text-white rounded-circle shadow">
                                            <i className="fas fa-clock" />
                                        </div>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <Card className="shadow border-0">
                    <CardHeader className="border-0 bg-white pb-0 pt-4 px-3 px-sm-4">
                        <Row className="align-items-center">
                            <Col>
                                <h3 className="mb-0 text-dark font-weight-bold">Verification Management</h3>
                            </Col>
                        </Row>
                    </CardHeader>

                    <CardBody className="px-3 px-sm-4 py-3">
                        <Row className="mb-3 align-items-center">
                            <Col xs="12" md="7" lg="6" className="mb-2 mb-md-0">
                                <InputGroup>
                                    <InputGroupAddon addonType="prepend">
                                        <InputGroupText>
                                            <i className="fas fa-search" />
                                        </InputGroupText>
                                    </InputGroupAddon>

                                    <Input
                                        type="search"
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        placeholder="Search guide by name or email"
                                        className="px-3 py-2"
                                    />
                                </InputGroup>
                            </Col>
                            <Col xs="12" sm="6" md="5" lg="3">
                                <CustomDropdown
                                    value={statusFilter}
                                    onChange={handleStatusFilterChange}
                                    options={[
                                        { label: "All Status", value: "all" },
                                        { label: "Approved", value: "approved" },
                                        { label: "Pending", value: "pending" },
                                        { label: "Rejected", value: "rejected" },
                                    ]}
                                />
                            </Col>
                        </Row>

                        {loading ? (
                            <div className="text-center py-5">
                                <i className="fas fa-spinner fa-spin fa-2x gradient-color-loader"></i>
                                <p className="mt-2">Loading guides...</p>
                            </div>
                        ) : guides.length === 0 ? (
                            <div className="text-center py-5">
                                <p className="text-muted">
                                    No guides found matching your criteria
                                </p>
                            </div>
                        ) : (
                            <div className="table-responsive guide-table-wrapper">
                                <Table className="align-items-center guide-table mb-0">
                                    <thead>
                                        <tr>
                                            <th>Sr. No.</th>
                                            <th>Guide</th>
                                            <th>Country</th>
                                            <th>Liveness Score</th>
                                            <th>Status</th>
                                            <th>Submitted</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {guides.map((guide, index) => (
                                            <tr key={guide.id}>
                                                {/* Sr. No. */}
                                                <td>
                                                    <span className="font-weight-bold text-sm">
                                                        {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                                                    </span>
                                                </td>

                                                {/* Guide */}
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <div className="guide-avatar mr-3">
                                                            {guide.name?.charAt(0)?.toUpperCase()}
                                                        </div>

                                                        <div>
                                                            <div className="guide-name">
                                                                {guide.name}
                                                            </div>

                                                            <div className="guide-email">
                                                                {guide.email}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* Location */}
                                                <td>
                                                    <span className="guide-location">
                                                        {guide.country}
                                                    </span>
                                                </td>

                                                {/* Liveness Score */}
                                                <td>
                                                    <span
                                                        className={`liveness-badge ${guide.livenessScore > 0.8
                                                            ? "liveness-success"
                                                            : "liveness-warning"
                                                            }`}
                                                    >
                                                        {(guide.livenessScore * 100).toFixed(0)}%
                                                    </span>
                                                </td>

                                                {/* Status */}
                                                <td>
                                                    <span
                                                        className={`status-badge status-${guide.status.toLowerCase()}`}
                                                    >
                                                        {guide.status}
                                                    </span>
                                                </td>

                                                {/* Submitted */}
                                                <td>
                                                    <span className="guide-date">
                                                        {new Date(
                                                            guide.createdAt
                                                        ).toLocaleDateString()}
                                                    </span>
                                                </td>

                                                {/* Actions */}
                                                <td className="text-right">
                                                    <UncontrolledDropdown
                                                        className="table-action-dropdown"
                                                    >
                                                        <DropdownToggle
                                                            className="btn-icon-only text-light"
                                                            color=""
                                                            role="button"
                                                            size="sm"
                                                        >
                                                            <i className="fas fa-ellipsis-v" />
                                                        </DropdownToggle>

                                                        <DropdownMenu
                                                            right

                                                        >
                                                            <DropdownItem onClick={() => handleViewGuide(guide.id)}>
                                                                <FiEye className="text-info mr-2" size={15} />
                                                                View Details
                                                            </DropdownItem>

                                                            {guide.status === "Pending" && (
                                                                <>
                                                                    <DropdownItem
                                                                        onClick={() => updateStatus(guide.uuid, "approved")}
                                                                        disabled={actionLoading || loading}
                                                                    >
                                                                        <FiCheck className="text-success mr-2" size={15} />
                                                                        Approve
                                                                    </DropdownItem>

                                                                    <DropdownItem
                                                                        className="text-danger"
                                                                        onClick={() => handleRejectClick(guide.uuid)}
                                                                        disabled={actionLoading || loading}
                                                                    >
                                                                        <FiX className="text-danger mr-2" size={15} />
                                                                        Reject
                                                                    </DropdownItem>
                                                                </>
                                                            )}
                                                        </DropdownMenu>
                                                    </UncontrolledDropdown>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </CardBody>

                    {!loading && totalItems > 0 && (
                        <CardFooter className="py-4 border-0">
                            <CustomPagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                totalItems={totalItems}
                                itemsPerPage={ITEMS_PER_PAGE}
                                onPageChange={handlePageChange}
                                itemName="guides"
                            />
                        </CardFooter>
                    )}
                </Card>


                <Modal
                    isOpen={modalOpen}
                    toggle={() => setModalOpen(false)}
                    size="lg"
                    scrollable
                    returnFocus={false}
                    className="user-table-user-model"
                >
                    <ModalHeader toggle={() => setModalOpen(false)}>
                        <div className="user-modal-header-content">
                            <div className="user-modal-header-icon">
                                <FiUser size={17} />
                            </div>

                            <div>
                                <h4 className="mb-0">{selectedGuide?.name || "Guide Details"}</h4>
                                <small>Guide Verification Details</small>
                            </div>
                        </div>
                    </ModalHeader>

                    <ModalBody>
                        {selectedGuide && (
                            <div className="user-modal-content">
                                <div className="user-profile-hero no-cover text-center mb-3">
                                    <div className="user-modal-avatar avatar-without-cover">
                                        {selectedGuide.profileImage || selectedGuide.profilePicture || selectedGuide.avatar || selectedGuide.image ? (
                                            <img
                                                src={selectedGuide.profileImage || selectedGuide.profilePicture || selectedGuide.avatar || selectedGuide.image}
                                                alt={selectedGuide.name || "Guide"}
                                                className="user-modal-profile-image"
                                                onError={(e) => {
                                                    e.currentTarget.style.display = "none";
                                                    if (e.currentTarget.nextElementSibling) e.currentTarget.nextElementSibling.style.display = "flex";
                                                }}
                                            />
                                        ) : null}
                                        <span
                                            style={{
                                                display: (selectedGuide.profileImage || selectedGuide.profilePicture || selectedGuide.avatar || selectedGuide.image) ? "none" : "flex",
                                            }}
                                        >
                                            {selectedGuide.name?.charAt(0)?.toUpperCase() || "G"}
                                        </span>
                                    </div>
                                    <h4 className="mb-0 font-weight-bold">{selectedGuide.name}</h4>
                                </div>

                                <div className="guide-modal-status">
                                    <span
                                        className={`guide-status-badge status-${selectedGuide.status.toLowerCase()}`}
                                    >
                                        {getStatusIcon(selectedGuide.status)}
                                        <span>{selectedGuide.status}</span>
                                    </span>

                                    {selectedGuide.status === "Pending" && (
                                        <span className="guide-status-message">
                                            Awaiting review
                                        </span>
                                    )}
                                </div>

                                <Nav className="guide-modal-tabs">
                                    <NavItem>
                                        <NavLink
                                            className={`guide-modal-tab ${activeTab === "1" ? "active" : ""
                                                }`}
                                            onClick={() => setActiveTab("1")}
                                        >
                                            <FiUser size={15} />
                                            <span>User Info</span>
                                        </NavLink>
                                    </NavItem>

                                    <NavItem>
                                        <NavLink
                                            className={`guide-modal-tab ${activeTab === "2" ? "active" : ""
                                                }`}
                                            onClick={() => setActiveTab("2")}
                                        >
                                            <FiFile size={15} />
                                            <span>Verification Docs</span>
                                        </NavLink>
                                    </NavItem>

                                    <NavItem>
                                        <NavLink
                                            className={`guide-modal-tab ${activeTab === "3" ? "active" : ""
                                                }`}
                                            onClick={() => setActiveTab("3")}
                                        >
                                            <FiCamera size={15} />
                                            <span>Selfie & Liveness</span>
                                        </NavLink>
                                    </NavItem>
                                </Nav>

                                <TabContent activeTab={activeTab} className="guide-modal-tab-content">

                                    <TabPane tabId="1">

                                        <div className="user-modal-section">

                                            <div className="user-modal-section-title">
                                                <i className="fas fa-user"></i>
                                                <span>Personal Information</span>
                                            </div>

                                            <Row>
                                                <Col xs="12" sm="6">
                                                    <div className="user-detail-item">
                                                        <div className="user-detail-icon">
                                                            <i className="fas fa-user"></i>
                                                        </div>

                                                        <div className="user-detail-content">
                                                            <span>Full Name</span>
                                                            <strong>{selectedGuide.name || "N/A"}</strong>
                                                        </div>
                                                    </div>
                                                </Col>

                                                <Col xs="12" sm="6">
                                                    <div className="user-detail-item">
                                                        <div className="user-detail-icon">
                                                            <i className="fas fa-envelope"></i>
                                                        </div>

                                                        <div className="user-detail-content">
                                                            <span>Email Address</span>
                                                            <strong>{selectedGuide.email || "N/A"}</strong>
                                                        </div>
                                                    </div>
                                                </Col>

                                                <Col xs="12" sm="6">
                                                    <div className="user-detail-item">
                                                        <div className="user-detail-icon">
                                                            <i className="fas fa-phone"></i>
                                                        </div>

                                                        <div className="user-detail-content">
                                                            <span>Mobile Number</span>
                                                            <strong>{selectedGuide.mobileNumber || "N/A"}</strong>
                                                        </div>
                                                    </div>
                                                </Col>

                                                <Col xs="12" sm="6">
                                                    <div className="user-detail-item">
                                                        <div className="user-detail-icon">
                                                            <i className="fas fa-venus-mars"></i>
                                                        </div>

                                                        <div className="user-detail-content">
                                                            <span>Gender</span>
                                                            <strong>{selectedGuide.gender || "N/A"}</strong>
                                                        </div>
                                                    </div>
                                                </Col>

                                                <Col xs="12" sm="6">
                                                    <div className="user-detail-item">
                                                        <div className="user-detail-icon">
                                                            <i className="fas fa-globe"></i>
                                                        </div>

                                                        <div className="user-detail-content">
                                                            <span>Country</span>
                                                            <strong>{selectedGuide.country || "N/A"}</strong>
                                                        </div>
                                                    </div>
                                                </Col>

                                                <Col xs="12" sm="6">
                                                    <div className="user-detail-item">
                                                        <div className="user-detail-icon">
                                                            <i className="fas fa-map-marker-alt"></i>
                                                        </div>

                                                        <div className="user-detail-content">
                                                            <span>Location</span>
                                                            <strong>{selectedGuide.city || "N/A"}</strong>
                                                        </div>
                                                    </div>
                                                </Col>

                                                <Col xs="12" sm="6">
                                                    <div className="user-detail-item">
                                                        <div className="user-detail-icon">
                                                            <i className="fas fa-user-tag"></i>
                                                        </div>

                                                        <div className="user-detail-content">
                                                            <span>User Type</span>

                                                            <Badge
                                                                className={`custom-badge ${getTypeBadgeClass(
                                                                    selectedGuide.type
                                                                )}`}
                                                                pill
                                                            >
                                                                {selectedGuide.type || "N/A"}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                </Col>

                                                <Col xs="12" sm="6">
                                                    <div className="user-detail-item">
                                                        <div className="user-detail-icon">
                                                            <i className="fas fa-toggle-on"></i>
                                                        </div>

                                                        <div className="user-detail-content">
                                                            <span>Account Status</span>

                                                            <Badge
                                                                className={`custom-badge ${getStatusBadgeClass(
                                                                    selectedGuide.isActive
                                                                )}`}
                                                                pill
                                                            >
                                                                {selectedGuide.isActive ? "Active" : "Inactive"}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                </Col>

                                            </Row>
                                        </div>

                                        <div className="user-modal-section guide-date-section">

                                            <div className="user-modal-section-title">
                                                <i className="fas fa-clock"></i>
                                                <span>Submission Details</span>
                                            </div>

                                            <Row>

                                                <Col xs="12" sm="6">
                                                    <div className="user-detail-item">
                                                        <div className="user-detail-icon">
                                                            <i className="fas fa-calendar-plus"></i>
                                                        </div>

                                                        <div className="user-detail-content">
                                                            <span>Submitted</span>
                                                            <strong>
                                                                {selectedGuide.createdAt
                                                                    ? new Date(
                                                                        selectedGuide.createdAt
                                                                    ).toLocaleString()
                                                                    : "N/A"}
                                                            </strong>
                                                        </div>
                                                    </div>
                                                </Col>

                                                <Col xs="12" sm="6">
                                                    <div className="user-detail-item">
                                                        <div className="user-detail-icon">
                                                            <i className="fas fa-calendar-check"></i>
                                                        </div>

                                                        <div className="user-detail-content">
                                                            <span>Last Updated</span>
                                                            <strong>
                                                                {selectedGuide.updatedAt
                                                                    ? new Date(
                                                                        selectedGuide.updatedAt
                                                                    ).toLocaleString()
                                                                    : "N/A"}
                                                            </strong>
                                                        </div>
                                                    </div>
                                                </Col>

                                                {selectedGuide.reviewedAt && (
                                                    <Col xs="12" sm="6">
                                                        <div className="user-detail-item">
                                                            <div className="user-detail-icon">
                                                                <i className="fas fa-check-circle"></i>
                                                            </div>

                                                            <div className="user-detail-content">
                                                                <span>Reviewed At</span>
                                                                <strong>
                                                                    {new Date(
                                                                        selectedGuide.reviewedAt
                                                                    ).toLocaleString()}
                                                                </strong>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                )}

                                            </Row>

                                            {selectedGuide.rejectionReason && (
                                                <div className="guide-rejection-box">
                                                    <div className="guide-rejection-icon">
                                                        <FiX size={17} />
                                                    </div>

                                                    <div>
                                                        <span>Rejection Reason</span>
                                                        <p>{selectedGuide.rejectionReason}</p>
                                                    </div>
                                                </div>
                                            )}

                                        </div>

                                    </TabPane>

                                    <TabPane tabId="2">

                                        <div className="user-modal-section">

                                            <div className="user-modal-section-title">
                                                <i className="fas fa-file-alt"></i>
                                                <span>Verification Documents</span>
                                            </div>

                                            <Row>

                                                <Col xs="12" md="6" className="mb-3 mb-md-0">
                                                    <div className="guide-document-card">
                                                        <div className="guide-document-header">
                                                            <span>Document Front</span>
                                                            <FiFile size={15} />
                                                        </div>

                                                        <div className="guide-document-preview">
                                                            {selectedGuide?.documentFrontImage ? (
                                                                <img
                                                                    key={selectedGuide.documentFrontImage}
                                                                    src={getImageUrl(selectedGuide.documentFrontImage, "guide-verification")}
                                                                    alt="Document Front"
                                                                    className="guide-document-image"
                                                                    onLoad={(e) => {
                                                                        e.currentTarget.style.display = "block";
                                                                        const err = e.currentTarget.parentElement?.querySelector(".guide-image-error");
                                                                        if (err) err.remove();
                                                                    }}
                                                                    onError={(e) => {
                                                                        console.error(
                                                                            "Document Front Image Failed:",
                                                                            e.currentTarget.src
                                                                        );

                                                                        e.currentTarget.style.display = "none";

                                                                        const parent = e.currentTarget.parentElement;

                                                                        if (
                                                                            parent &&
                                                                            !parent.querySelector(".guide-image-error")
                                                                        ) {
                                                                            const errorMsg = document.createElement("p");
                                                                            errorMsg.className = "guide-image-error";
                                                                            errorMsg.textContent = "Image failed to load";
                                                                            parent.appendChild(errorMsg);
                                                                        }
                                                                    }}
                                                                />
                                                            ) : (
                                                                <div className="guide-no-image">
                                                                    <FiFile size={28} />
                                                                    <span>No image available</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </Col>

                                                <Col xs="12" md="6">
                                                    <div className="guide-document-card">
                                                        <div className="guide-document-header">
                                                            <span>Document Back</span>
                                                            <FiFile size={15} />
                                                        </div>

                                                        <div className="guide-document-preview">
                                                            {selectedGuide?.documentBackImage ? (
                                                                <img
                                                                    key={selectedGuide.documentBackImage}
                                                                    src={getImageUrl(selectedGuide.documentBackImage, "guide-verification")}
                                                                    alt="Document Back"
                                                                    className="guide-document-image"
                                                                    onLoad={(e) => {
                                                                        e.currentTarget.style.display = "block";
                                                                        const err = e.currentTarget.parentElement?.querySelector(".guide-image-error");
                                                                        if (err) err.remove();
                                                                    }}
                                                                    onError={(e) => {
                                                                        console.error(
                                                                            "Document Back Image Failed:",
                                                                            e.currentTarget.src
                                                                        );

                                                                        e.currentTarget.style.display = "none";

                                                                        const parent = e.currentTarget.parentElement;

                                                                        if (
                                                                            parent &&
                                                                            !parent.querySelector(".guide-image-error")
                                                                        ) {
                                                                            const errorMsg = document.createElement("p");
                                                                            errorMsg.className = "guide-image-error";
                                                                            errorMsg.textContent = "Image failed to load";
                                                                            parent.appendChild(errorMsg);
                                                                        }
                                                                    }}
                                                                />
                                                            ) : (
                                                                <div className="guide-no-image">
                                                                    <FiFile size={28} />
                                                                    <span>No image available</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </Col>

                                            </Row>

                                        </div>

                                    </TabPane>

                                    {/* Tab 3: Selfie & Liveness */}
                                    <TabPane tabId="3">
                                        <div className="pt-2">
                                            <Row>
                                                <Col xs="12" md="6" className="mb-3 mb-md-0">
                                                    <div className="user-modal-section">
                                                        <div className="user-modal-section-title">
                                                            <FiCamera size={15} />
                                                            <span>Selfie Image</span>
                                                        </div>

                                                        <div className="guide-document-preview guide-selfie-preview">
                                                            {selectedGuide?.selfieImage ? (
                                                                <img
                                                                    key={selectedGuide.selfieImage}
                                                                    src={getImageUrl(selectedGuide.selfieImage, "guide-verification")}
                                                                    alt="Selfie"
                                                                    className="guide-document-image"
                                                                    onLoad={(e) => {
                                                                        e.currentTarget.style.display = "block";
                                                                        const err = e.currentTarget.parentElement?.querySelector(".guide-image-error");
                                                                        if (err) err.remove();
                                                                    }}
                                                                    onError={(e) => {
                                                                        console.error(
                                                                            "Selfie Image Failed:",
                                                                            e.currentTarget.src
                                                                        );

                                                                        e.currentTarget.style.display = "none";

                                                                        const parent = e.currentTarget.parentElement;

                                                                        if (
                                                                            parent &&
                                                                            !parent.querySelector(".guide-image-error")
                                                                        ) {
                                                                            const errorMsg = document.createElement("p");
                                                                            errorMsg.className = "guide-image-error";
                                                                            errorMsg.textContent = "Image failed to load";
                                                                            parent.appendChild(errorMsg);
                                                                        }
                                                                    }}
                                                                />
                                                            ) : (
                                                                <div className="guide-no-image">
                                                                    <FiCamera size={28} />
                                                                    <span>No image available</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </Col>

                                                <Col xs="12" md="6">
                                                    <div className="user-modal-section">
                                                        <div className="user-modal-section-title">
                                                            <FiCheckCircle size={15} />
                                                            <span>Liveness Results</span>
                                                        </div>

                                                        <div className="guide-liveness-card">

                                                            <div className="guide-liveness-row">
                                                                <div>
                                                                    <span>Liveness Passed</span>
                                                                    <strong>
                                                                        {selectedGuide.livenessPassed
                                                                            ? "Verification successful"
                                                                            : "Verification failed"}
                                                                    </strong>
                                                                </div>

                                                                <Badge
                                                                    className={`custom-badge ${selectedGuide.livenessPassed
                                                                        ? "badge-active"
                                                                        : "badge-pending"
                                                                        }`}
                                                                    pill
                                                                >
                                                                    {selectedGuide.livenessPassed ? "Yes" : "No"}
                                                                </Badge>
                                                            </div>

                                                            <div className="guide-score-section">
                                                                <div className="guide-score-header">
                                                                    <span>Liveness Score</span>

                                                                    <strong>
                                                                        {(
                                                                            (selectedGuide.livenessScore || 0) *
                                                                            100
                                                                        ).toFixed(0)}
                                                                        %
                                                                    </strong>
                                                                </div>

                                                                <div className="guide-score-bar">
                                                                    <div
                                                                        className={`guide-score-progress ${selectedGuide.livenessScore > 0.8
                                                                            ? "score-high"
                                                                            : selectedGuide.livenessScore > 0.6
                                                                                ? "score-medium"
                                                                                : "score-low"
                                                                            }`}
                                                                        style={{
                                                                            width: `${(selectedGuide.livenessScore || 0) * 100
                                                                                }%`,
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>

                                                        </div>

                                                    </div>
                                                </Col>

                                            </Row>
                                        </div>
                                    </TabPane>

                                </TabContent>
                            </div>
                        )}
                    </ModalBody>

                    <ModalFooter>

                        {selectedGuide?.status === "Pending" && (
                            <>
                                <Button
                                    className="user-action-btn user-edit-btn"
                                    size="sm"
                                    onClick={() =>
                                        updateStatus(selectedGuide.uuid, "approved")
                                    }
                                    disabled={actionLoading}
                                >
                                    <FiCheck size={15} />
                                    Approve
                                </Button>

                                <Button
                                    className="user-action-btn user-delete-btn"
                                    size="sm"
                                    onClick={() => {
                                        setModalOpen(false);
                                        handleRejectClick(selectedGuide.uuid);
                                    }}
                                    disabled={actionLoading}
                                >
                                    <FiX size={15} />
                                    Reject
                                </Button>
                            </>
                        )}

                        <Button
                            className="user-action-btn user-view-btn"
                            size="sm"
                            onClick={() => setModalOpen(false)}
                        >
                            <FiX size={15} />
                            Close
                        </Button>

                    </ModalFooter>
                </Modal>


                {/* Reject Reason Modal */}
                <Modal isOpen={showRejectModal} toggle={() => setShowRejectModal(false)} returnFocus={false}>
                    <ModalHeader toggle={() => setShowRejectModal(false)}>
                        <FiX className="text-danger mr-2" /> Reject Guide Verification
                    </ModalHeader>
                    <ModalBody>
                        <p>Please provide a reason for rejecting this guide verification:</p>
                        <Input
                            type="textarea"
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            placeholder="Enter rejection reason..."
                            rows="4"
                        />
                        <small className="text-muted">This reason will be visible to the guide.</small>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setShowRejectModal(false)} disabled={actionLoading}>
                            Cancel
                        </Button>
                        <Button
                            color="danger"
                            onClick={handleConfirmReject}
                            disabled={actionLoading || !rejectionReason.trim()}
                        >
                            {actionLoading ? 'Processing...' : 'Confirm Reject'}
                        </Button>
                    </ModalFooter>
                </Modal>
            </Container>
        </>
    );
};

export default GuideManagement;